function roundCurrency(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.round((numericValue + Number.EPSILON) * 100) / 100;
}

function getCategoryGroupKey(category, rate) {
  const normalized = String(category || '').toLowerCase().replace(/[\s_-]/g, '');
  if (normalized.includes('zero')) return 'zeroRated';
  if (normalized.includes('exempt') || normalized.includes('custom')) return 'exemptCustom';
  if (normalized.includes('standard') || Number(rate) > 0) return 'standardRated';
  return 'exemptCustom';
}

function getTaxSummaryGroups(taxSummary) {
  if (!taxSummary) return {};

  if (taxSummary.groups) {
    return taxSummary.groups;
  }

  const groups = {};
  (taxSummary.breakdownByCategory || []).forEach((category) => {
    const groupKey = getCategoryGroupKey(category.taxCategory, category.taxRate);
    const current = groups[groupKey] || {
      inclusiveTotal: 0,
      gctIncluded: 0,
    };

    current.inclusiveTotal = roundCurrency(
      current.inclusiveTotal + Number(category.taxInclusiveTotal || 0)
    );
    current.gctIncluded = roundCurrency(
      current.gctIncluded + Number(category.gctIncludedTotal || 0)
    );
    groups[groupKey] = current;
  });

  return groups;
}

/**
 * Build checkout payload passed through WiPay `data` / simulated redirect query.
 * @param {Object} params
 */
export function buildCheckoutData({
  userId,
  cartDocId,
  supermarketId,
  cartInstanceId,
  supermarketName,
  cartItems,
  confirmationFee,
  orderSubtotal,
  taxSummary,
  gctSummary,
  isFullPayment,
  deliveryOption,
  deliveryAddress,
  deliveryPhone,
  deliveryCheckoutFields = {},
  wipayOrderId,
}) {
  const persistedTaxSummary = taxSummary || gctSummary || null;
  const gctIncludedTotal =
    Number(persistedTaxSummary?.totals?.gctIncluded) ||
    Number(persistedTaxSummary?.gctIncludedTotal) ||
    0;

  const base = {
    userId,
    cartDocId,
    supermarketId,
    cartInstanceId,
    supermarketName: supermarketName || 'N/A',
    cartItems,
    confirmationFee,
    orderSubtotal,
    taxInclusiveTotal: orderSubtotal,
    gctIncludedTotal,
    taxSummary: persistedTaxSummary,
    gctSummary: persistedTaxSummary,
    paymentType: isFullPayment ? 'full' : 'confirmation_fee_only',
    deliveryOption,
    deliveryAddress: deliveryOption === 'delivery' ? deliveryAddress : null,
    deliveryPhone: deliveryOption === 'delivery' ? deliveryPhone : null,
    ...deliveryCheckoutFields,
  };

  if (wipayOrderId) {
    base.wipayOrderId = wipayOrderId;
  }

  return base;
}

/**
 * WiPay gateway payload: camelCase fields plus snake_case compatibility keys.
 *
 * @param {Object} checkoutData Full checkout payload from {@link buildCheckoutData}.
 * @param {Object} [options]
 * @param {boolean} [options.compact] When true, emit only a bounded set of
 *   identifiers and scalar fields. WiPay echoes this `data` blob back on the
 *   redirect URL, so the full payload (cart items + GCT line-item breakdowns +
 *   stringified duplicates) can push the return URL past Firebase Hosting's URI
 *   length limit and trigger an HTTP 414 ("URI Too Long") before the SPA loads.
 *   The compact payload keeps the return URL small; the callback recovers cart
 *   items and the tax breakdown from the persisted cart instance snapshot.
 */
export function buildWipayGatewayData(checkoutData, { compact = false } = {}) {
  const {
    userId,
    cartDocId,
    supermarketId,
    cartInstanceId,
    wipayOrderId,
    supermarketName,
    cartItems,
    confirmationFee,
    orderSubtotal,
    paymentType,
    deliveryOption,
    deliveryAddress,
    deliveryPhone,
    deliveryFee,
    maxDeliveryDistance,
    taxInclusiveTotal,
    gctIncludedTotal,
    taxSummary,
    gctSummary,
  } = checkoutData;
  const persistedTaxSummary = taxSummary || gctSummary || null;
  const resolvedTaxInclusiveTotal = taxInclusiveTotal ?? orderSubtotal;
  const resolvedGctIncludedTotal =
    gctIncludedTotal ??
    persistedTaxSummary?.totals?.gctIncluded ??
    persistedTaxSummary?.gctIncludedTotal;

  if (compact) {
    // Bounded payload only. Do NOT include cart items, line-item tax
    // breakdowns, or their stringified duplicates here; they are persisted on
    // the cart instance and recovered by the callback from there.
    return {
      userId,
      cartDocId,
      supermarketId,
      cartInstanceId,
      wipayOrderId,
      supermarketName,
      confirmationFee,
      orderSubtotal,
      taxInclusiveTotal: resolvedTaxInclusiveTotal,
      gctIncludedTotal: resolvedGctIncludedTotal,
      paymentType,
      deliveryOption,
      deliveryAddress,
      deliveryPhone,
      deliveryFee,
      maxDeliveryDistance,
      user_id: userId,
      supermarket_id: supermarketId,
      supermarket_name: supermarketName,
      order_subtotal: orderSubtotal,
      tax_inclusive_total: resolvedTaxInclusiveTotal,
      gct_included_total: resolvedGctIncludedTotal,
      payment_type: paymentType,
      delivery_option: deliveryOption,
      delivery_address: deliveryAddress,
      delivery_phone: deliveryPhone,
      delivery_fee: deliveryFee,
      max_delivery_distance: maxDeliveryDistance,
    };
  }

  const taxSummaryGroups = getTaxSummaryGroups(persistedTaxSummary);

  return {
    ...checkoutData,
    taxSummary: persistedTaxSummary,
    gctSummary: persistedTaxSummary,
    user_id: userId,
    supermarket_id: supermarketId,
    supermarket_name: supermarketName,
    cart_items: JSON.stringify(cartItems || []),
    order_subtotal: orderSubtotal,
    tax_inclusive_total: resolvedTaxInclusiveTotal,
    gct_included_total: resolvedGctIncludedTotal,
    tax_summary: persistedTaxSummary ? JSON.stringify(persistedTaxSummary) : undefined,
    gct_summary: JSON.stringify(persistedTaxSummary || null),
    standard_rated_total: taxSummaryGroups.standardRated?.inclusiveTotal,
    zero_rated_total: taxSummaryGroups.zeroRated?.inclusiveTotal,
    exempt_custom_total: taxSummaryGroups.exemptCustom?.inclusiveTotal,
    payment_type: paymentType,
    delivery_option: deliveryOption,
    delivery_address: deliveryAddress,
    delivery_phone: deliveryPhone,
    delivery_fee: deliveryFee,
    max_delivery_distance: maxDeliveryDistance,
    wipayOrderId,
    cartInstanceId,
  };
}
